import { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import SignUpIcon from '@mui/icons-material/HowToReg'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'

import { SettingsDropdownMobile, Switch } from 'src/components'
import {
  useAllowScanToErnSetting,
  useUpdateSignUpSettings,
  useUpdateTraicyDefinition,
} from 'src/hooks'

import { ISettingsRespDto, ESettingsPromptFields } from 'src/types'
import { SettingsItem } from './settings-item.component'
import { Box } from '@mui/material'
import { SettingsHashTags } from '../hash-tags'
import { EPromptsVariables } from 'src/constants'

interface IProps {
  settings: ISettingsRespDto | null
  isSettingsFetching: boolean
  isSettingsLoading: boolean
}

const MobileSettings = ({ settings, isSettingsFetching, isSettingsLoading }: IProps) => {
  const [t] = useTranslation()
  const { mutate: updateSignUp, isLoading: signUpIsLoading } = useUpdateSignUpSettings()
  const { mutate: updateTraicyPrompt, isLoading: isTraicyPromptLoading } =
    useUpdateTraicyDefinition()

  const handleUpdateSignUp = (e: ChangeEvent<HTMLInputElement>) => {
    updateSignUp({ enabled: e.target.checked })
  }
  const { mutate: scanToEarnSetting, isLoading: isScanToEarnSettingLoading } =
    useAllowScanToErnSetting()

  const handleUpdateTraicyPrompt = (text: string, field: ESettingsPromptFields) => {
    updateTraicyPrompt({ text, field })
  }

  const handleUpdateScanToEarn = (e: ChangeEvent<HTMLInputElement>) => {
    scanToEarnSetting({ enabled: e.target.checked })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <SettingsDropdownMobile
        icon={SignUpIcon}
        label={t('sign-up-settings-label.v')}
        rightNode={
          <Switch
            checked={settings?.sign_up_enabled}
            disabled={signUpIsLoading || isSettingsFetching}
            onChange={handleUpdateSignUp}
          />
        }
      />
      <SettingsDropdownMobile
        icon={AddCircleOutlineIcon}
        label={t('scan-to-earn-allow-settings-label.v')}
        rightNode={
          <Switch
            checked={settings?.scan_to_earn_enabled}
            disabled={isScanToEarnSettingLoading || isSettingsFetching}
            onChange={handleUpdateScanToEarn}
          />
        }
      />
      <SettingsItem
        label={t('settings-traicy-definition-label.v')}
        text={settings?.traicy_definition || ''}
        field={ESettingsPromptFields.TRAICY_DEFINITION}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        isMobile
      />
      <SettingsItem
        label={t('settings-traicy-post-label.v')}
        text={settings?.traicy_post || ''}
        field={ESettingsPromptFields.TRAICY_POST}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        isMobile
      />
      <SettingsItem
        label={t('settings-traicy-system-hash-tag-reply-label.v')}
        text={settings?.traicy_definition_hash_tag_reply || ''}
        field={ESettingsPromptFields.TRAICY_DEFINITION_HASHTAG_REPLY}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        isMobile
      />
      <SettingsItem
        label={t('settings-traicy-hash-tag-reply-label.v')}
        text={settings?.traicy_hash_tag_reply || ''}
        field={ESettingsPromptFields.TRAICY_HASHTAG_REPLY}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        isMobile
        description={`Use the ${EPromptsVariables.REPLY_VARIABLE} variable here, which will be replaced with the actual message of the tweet.`}
      />

      <SettingsHashTags
        tags={settings?.traicy_hash_tag_list || []}
        label={t('settings-traicy-system-hash-tag-list-label.v')}
        isMobile
      />

      <SettingsItem
        label={t('settings-traicy-system-mentions-reply-label.v')}
        text={settings?.traicy_definition_mentions_reply || ''}
        field={ESettingsPromptFields.TRAICY_DEFINITION_MENTIONS_REPLY}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        isMobile
      />
      <SettingsItem
        label={t('settings-traicy-mentions-reply-label.v')}
        text={settings?.traicy_mentions_reply || ''}
        field={ESettingsPromptFields.TRAICY_MENTIONS_REPLY}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        description={`Use the ${EPromptsVariables.COMMON_TRAICY_VARIABLE} variable here, which will be replaced with the actual message of the tweet.`}
        isMobile
      />
      <SettingsItem
        label={t('settings-traicy-system-message-check-label.v')}
        text={settings?.traicy_definition_message_check || ''}
        field={ESettingsPromptFields.TRAICY_DEFINITION_MESSAGE_CHECK}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        description="This assistant is designed to classify tweets mentioning Traicy. It should determine if the message is valid and worthy of a reply by answering 'YES' (in all caps, without quotes)."
        isMobile
      />
      <SettingsItem
        label={t('settings-traicy-message-check-label.v')}
        text={settings?.traicy_message_check || ''}
        field={ESettingsPromptFields.TRAICY_MESSAGE_CHECK}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        description={`Use the ${EPromptsVariables.MENTION_TRAICY_VARIABLE} variable here, which will be replaced with the actual message of the tweet.`}
        isMobile
      />

      <SettingsItem
        label={t('settings-traicy-definition-chatbot-label.v')}
        text={settings?.traicy_definition_chatbot || ''}
        field={ESettingsPromptFields.TRAICY_DEFINITION_CHATBOT}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        isMobile
      />
      <SettingsItem
        label={t('settings-traicy-reply-chatbot-label.v')}
        text={settings?.traicy_reply_chatbot || ''}
        field={ESettingsPromptFields.TRAICY_REPLY_CHATBOT}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        description={`Use the ${EPromptsVariables.CHATBOT_REPLY_VARIABLE} variable here, which will be replaced with the actual user message.`}
        isMobile
      />

      <SettingsItem
        label={'Scan-to-earn definition (system): Media Validation'}
        text={settings?.scan_to_earn_definition_media_validation || ''}
        field={ESettingsPromptFields.SCAN_TO_EARN_DEFINITION_MEDIA_VALIDATION}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        description={`Use the ${EPromptsVariables.SCAN_TO_EARN_BARCODE_VARIABLE} variable here, which will be replaced with the actual product Barcode ID .`}
        isMobile
      />
      <SettingsItem
        label={'Prompt (user): Media Validation'}
        text={settings?.scan_to_earn_media_validation || ''}
        field={ESettingsPromptFields.SCAN_TO_EARN_MEDIA_VALIDATION}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        isMobile
      />

      <SettingsItem
        label={'Scan-to-earn definition (system): Product Name'}
        text={settings?.scan_to_earn_definition_product_name || ''}
        field={ESettingsPromptFields.SCAN_TO_EARN_DEFINITION_PRODUCT_NAME}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        isMobile
      />
      <SettingsItem
        label={'Prompt (user): Product Name'}
        text={settings?.scan_to_earn_product_name || ''}
        field={ESettingsPromptFields.SCAN_TO_EARN_PRODUCT_NAME}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        isMobile
      />

      <SettingsItem
        label={'Scan-to-earn definition (system): Company Name'}
        text={settings?.scan_to_earn_definition_company_name || ''}
        field={ESettingsPromptFields.SCAN_TO_EARN_DEFINITION_COMPANY_NAME}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        isMobile
      />

      <SettingsItem
        label={'Prompt (user): Company Name'}
        text={settings?.scan_to_earn_company_name || ''}
        field={ESettingsPromptFields.SCAN_TO_EARN_COMPANY_NAME}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        isMobile
      />

      <SettingsItem
        label={'Scan-to-earn definition (system): Summary'}
        text={settings?.scan_to_earn_definition_summary || ''}
        field={ESettingsPromptFields.SCAN_TO_EARN_DEFINITION_SUMMARY}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        isMobile
      />
      <SettingsItem
        label={'Prompt (user): Summary'}
        text={settings?.scan_to_earn_summary || ''}
        field={ESettingsPromptFields.SCAN_TO_EARN_SUMMARY}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        isMobile
      />

      <SettingsItem
        label={'Scan-to-earn definition (system): Product Type'}
        text={settings?.scan_to_earn_definition_product_type || ''}
        field={ESettingsPromptFields.SCAN_TO_EARN_DEFINITION_PRODUCT_TYPE}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        isMobile
      />

      <SettingsItem
        label={'Prompt (user): Product Type'}
        text={settings?.scan_to_earn_product_type || ''}
        field={ESettingsPromptFields.SCAN_TO_EARN_PRODUCT_TYPE}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        isMobile
      />

      <SettingsItem
        label={'Scan-to-earn definition (system): Impacts'}
        text={settings?.scan_to_earn_definition_impacts || ''}
        field={ESettingsPromptFields.SCAN_TO_EARN_DEFINITION_IMPACTS}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        description={`Use the ${EPromptsVariables.SCAN_TO_EARN_IMPACTS_LIST_VARIABLE} variable here, which will be replaced with the full list of impacts from the 'impact_library' table.`}
        isMobile
      />
      <SettingsItem
        label={'Prompt (user): Impacts'}
        text={settings?.scan_to_earn_impacts || ''}
        field={ESettingsPromptFields.SCAN_TO_EARN_IMPACTS}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        isMobile
      />

      <SettingsItem
        label={'Scan-to-earn definition (system): Account Info'}
        text={settings?.scan_to_earn_definition_account_info || ''}
        field={ESettingsPromptFields.SCAN_TO_EARN_DEFINITION_ACCOUNT_INFO}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        isMobile
      />
      <SettingsItem
        label={'Prompt (user): Account Info'}
        text={settings?.scan_to_earn_account_info || ''}
        field={ESettingsPromptFields.SCAN_TO_EARN_ACCOUNT_INFO}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        description={`The following variables can be used:
            
             ${EPromptsVariables.SCAN_TO_EARN_ACCOUNT_NAME_VARIABLE} (required), ${EPromptsVariables.SCAN_TO_EARN_PRODUCT_NAME_VARIABLE}, ${EPromptsVariables.SCAN_TO_EARN_PRODUCT_TYPE_VARIABLE}, ${EPromptsVariables.SCAN_TO_EARN_PRODUCT_DESCRIPTION_VARIABLE}, ${EPromptsVariables.SCAN_TO_EARN_SCANNER_LATITUDE_VARIABLE}, ${EPromptsVariables.SCAN_TO_EARN_SCANNER_LONGITUDE_VARIABLE} `}
        isMobile
      />

      <SettingsItem
        label={'Language Translation(system)'}
        text={settings?.language_translation_definition || ''}
        field={ESettingsPromptFields.LANGUAGE_TRANSLATION_DEFINITION}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        isMobile
      />
      <SettingsItem
        label={'Prompt (user): Language Translation'}
        text={settings?.language_translation || ''}
        field={ESettingsPromptFields.LANGUAGE_TRANSLATION}
        onSave={handleUpdateTraicyPrompt}
        isLoading={isTraicyPromptLoading || isSettingsLoading}
        description={`The following variables can be used:
                  
                   ${EPromptsVariables.LANGUAGE_TO_TRANSLATE}, 
                   ${EPromptsVariables.VALUE_TO_TRANSLATE}`}
        isMobile
      />
    </Box>
  )
}

export { MobileSettings }
